/** How many suggestions the unknown-command state offers (需求 FR-14 §4.2.3). */
export const COMMAND_SUGGESTION_LIMIT = 3;

/** Edit distance beyond which two command names are not a plausible typo. */
const MAX_DISTANCE = 3;

/** Levenshtein distance, two rows rather than a full matrix. */
function editDistance(a: string, b: string): number {
  if (a === b) {
    return 0;
  }
  if (a.length === 0 || b.length === 0) {
    return Math.max(a.length, b.length);
  }
  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const current = [i];
    for (let j = 1; j <= b.length; j += 1) {
      const substitution = previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1);
      current[j] = Math.min(current[j - 1] + 1, previous[j] + 1, substitution);
    }
    previous = current;
  }
  return previous[b.length];
}

/** A command name (or alias) a suggestion can point at, plus what to run. */
export interface SuggestionCandidate<T> {
  /** The name as the user would type it, without the `/` prefix. */
  readonly name: string;
  readonly value: T;
}

/**
 * Closest matches for an unrecognised command (需求 FR-14 §4.2.3 建议命令算法).
 *
 * Prefix hits first — typing `/op` is an unfinished word, not a misspelling —
 * then near misses by edit distance. Candidates should include aliases and the
 * synthesised `/open <module>` targets, so `/opne im` still finds its mark.
 */
export function suggestCommands<T>(
  typed: string,
  candidates: readonly SuggestionCandidate<T>[],
  limit = COMMAND_SUGGESTION_LIMIT,
): T[] {
  const needle = typed.trim().toLowerCase();
  if (needle.length === 0) {
    return [];
  }

  const scored = candidates
    .map((candidate) => {
      const name = candidate.name.toLowerCase();
      return {
        value: candidate.value,
        isPrefix: name.startsWith(needle),
        distance: editDistance(needle, name),
        name,
      };
    })
    .filter(
      (entry) =>
        entry.isPrefix ||
        entry.name.includes(needle) ||
        entry.distance <= MAX_DISTANCE,
    )
    .sort((a, b) => {
      if (a.isPrefix !== b.isPrefix) {
        return a.isPrefix ? -1 : 1;
      }
      if (a.distance !== b.distance) {
        return a.distance - b.distance;
      }
      return a.name.localeCompare(b.name);
    });

  const seen = new Set<T>();
  const picked: T[] = [];
  for (const entry of scored) {
    // Aliases and main names of the same command both land here; the first
    // (best-scoring) spelling wins and the rest are dropped.
    if (seen.has(entry.value)) {
      continue;
    }
    seen.add(entry.value);
    picked.push(entry.value);
    if (picked.length >= limit) {
      break;
    }
  }
  return picked;
}
